/* Conversation collections are shared explicitly; personal saves are independent. */
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  else Object.defineProperty(root, "LouderrSharedCollections", {value:Object.freeze(api)});
})(globalThis, function () {
  "use strict";
  function create(hooks) {
    const doc = hooks.document || document;
    const el = Object.fromEntries(["dialog", "title", "subject", "status", "error", "back", "refresh", "list", "more",
      "form", "name", "sharing", "consent", "submit", "cancel-edit", "actions", "rename", "delete", "add",
      "confirmation", "confirm-text", "confirm", "keep", "retry"]
      .map(key => [key, doc.getElementById("shared-collection-" + key)]));
    const owner = () => hooks.viewer()?.id || null;
    const scope = () => hooks.scope?.() || null;
    let identity = owner(), accountScope = scope(), epoch = 0, session = null;
    // A lost create response keeps its exact token across closing/reopening.
    // No mutation is replayed automatically, and no new create replaces it.
    const attempts = new Map();
    const accountCurrent = s => s.owner === owner() && s.scope === scope() && s.epoch === epoch;
    const current = s => Boolean(s && session === s && accountCurrent(s) && el.dialog.open);
    const attemptFor = s => s?.conversation ? attempts.get(s.conversation.id) : null;
    const available = s => current(s) && hooks.canRead() && s.ready && !s.loading && !attemptFor(s);
    const writable = s => available(s) && hooks.canWrite();
    const removable = s => available(s) && hooks.canRemove();
    const base = s => `/api/messages/conversations/${encodeURIComponent(s.conversation.id)}/collections`;
    const detailPath = (s, id) => `${base(s)}/${encodeURIComponent(id)}`;
    const make = (tag, text = "", className = "") => {
      const node = doc.createElement(tag); node.textContent = text; node.className = className; return node;
    };
    function button(text, action, className = "secondary-button") {
      const node = make("button", text, className); node.type = "button";
      node.addEventListener("click", action); return node;
    }
    const conversationName = c => c.title || c.participant?.display_name || c.participant?.handle || "Conversation";
    const eligible = c => c?.id && c.request_state === "accepted" && !c.blocked && !c.read_only
      && (c.kind !== "group" || c.member_state === "joined");
    function collection(value, cid) {
      if (!value || typeof value.id !== "string" || value.conversation_id !== cid || typeof value.name !== "string"
          || !Number.isSafeInteger(value.revision) || value.revision < 1
          || value.sharing_scope !== "current_and_future_members") throw new Error("Collection details could not be verified. Refresh to continue.");
      return value;
    }
    function collectionList(data, cid) {
      if (!Array.isArray(data?.items) || data.items.length > 50) throw new Error("Collections could not be verified.");
      const rows = data.items.map(item => collection(item, cid));
      if (new Set(rows.map(item => item.id)).size !== rows.length) throw new Error("Collections could not be verified.");
      return rows;
    }
    function page(data, s) {
      const item = collection(data?.collection, s.conversation.id);
      if (item.id !== s.collectionId || !Array.isArray(data.items) || data.items.length > 50
          || data.items.some(post => !post || typeof post.id !== "string")
          || new Set(data.items.map(post => post.id)).size !== data.items.length
          || data.next_after != null && (!data.items.length || data.next_after !== data.items.at(-1).id)) {
        throw new Error("Collection posts could not be verified. Refresh to continue.");
      }
      return data;
    }
    function clearContent(s) {
      s.items = []; s.collections = []; s.collection = null; s.next = null; s.ready = false;
    }
    function deny(s) {
      clearContent(s); s.conversations = []; s.confirmation = null; s.editing = false; s.draft = ""; s.drop = null;
      if (s.conversation) s.conversation = {id:s.conversation.id};
    }
    function syncIdentity() {
      if (identity === owner() && accountScope === scope() && hooks.canRead()) return false;
      identity = owner(); accountScope = scope(); ++epoch; attempts.clear();
      close(); el.list.replaceChildren(); el.name.value = ""; el.consent.checked = false;
      for (const key of ["subject", "status", "error", "confirm-text"]) el[key].textContent = "";
      return true;
    }
    function paint(s) {
      if (!current(s)) return;
      const op = attemptFor(s), browsing = !s.conversation, detail = Boolean(s.collectionId);
      el.title.textContent = browsing ? "Choose a conversation" : s.collection?.name || "Shared collections";
      el.subject.textContent = browsing ? `Choose an accepted conversation for ${s.drop?.title ? `“${s.drop.title}”` : "this post"}.`
        : `${conversationName(s.conversation)} · Current and future members who join can view and edit these collections.`;
      el.status.textContent = s.loading ? "Loading…" : op?.busy ? "Saving…"
        : op?.unknown ? `The creation of “${op.name}” could not be confirmed. Refresh, then retry that same creation.`
        : s.notice || "Posts keep their original audience. Your personal saved folders stay private.";
      el.error.textContent = s.error || ""; el.error.hidden = !s.error;
      el.refresh.disabled = s.loading || Boolean(op?.busy);
      el.back.hidden = browsing || !detail && !s.drop;
      el.back.disabled = s.loading || Boolean(op?.busy);
      el.more.hidden = !s.next; el.more.disabled = !available(s);
      el.more.textContent = browsing ? "More conversations" : "More posts";
      el.form.hidden = browsing || detail && !s.editing || !hooks.canWrite();
      if (el.name.value !== s.draft) el.name.value = s.draft;
      // Reading another member's update must not blur an active text field.
      // Only submission waits for fresh revision authority; drafts stay editable.
      el.name.disabled = !hooks.canWrite() || Boolean(op);
      el.submit.textContent = s.editing ? "Save name" : "Create shared collection";
      el.submit.disabled = !writable(s) || !s.editing && (s.collections.length >= 50 || !s.consent);
      el.sharing.hidden = Boolean(s.editing); el.consent.checked = s.consent;
      el.consent.disabled = !hooks.canWrite() || Boolean(op);
      el["cancel-edit"].hidden = !s.editing; el["cancel-edit"].disabled = s.loading || Boolean(op?.busy);
      el.actions.hidden = !detail || !s.collection;
      el.rename.disabled = !writable(s); el.delete.disabled = !removable(s);
      el.add.hidden = !detail || !s.drop; el.add.disabled = !writable(s) || !s.collection;
      el.add.textContent = "Add this post to collection";
      el.confirmation.hidden = !s.confirmation;
      el["confirm-text"].textContent = s.confirmation
        ? s.confirmation.kind === "delete" ? `Delete “${s.collection?.name || "this collection"}” for everyone? Original posts and personal saves stay unchanged.`
          : `Remove “${s.confirmation.title}” from this collection for everyone? The original post stays unchanged.` : "";
      el.confirm.disabled = !removable(s) || !s.collection; el.keep.disabled = Boolean(op?.busy);
      el.retry.hidden = !op?.unknown || op.kind !== "create";
      el.retry.disabled = s.loading || !op?.readback || !hooks.canWrite() || Boolean(op?.busy);
      el.list.replaceChildren();
      if (!s.ready) return;
      if (browsing) {
        for (const c of s.conversations) {
          const row = button(conversationName(c), () => chooseConversation(s, c), "shared-collection-choice");
          row.disabled = !available(s); el.list.append(row);
        }
        if (!s.conversations.length) el.list.append(make("p", s.next ? "No eligible conversations on this page. Continue below."
          : "No accepted conversations yet. Start a conversation in Messages first.", "metric-note"));
      } else if (!detail) {
        for (const item of s.collections) {
          const row = button(item.name, () => openCollection(s, item.id), "shared-collection-choice");
          row.setAttribute("aria-label", `Open collection ${item.name}`); row.disabled = !available(s); el.list.append(row);
        }
        if (!s.collections.length) el.list.append(make("p", "No shared collections yet.", "metric-note"));
        if (s.collections.length >= 50) el.list.append(make("p", "This conversation has 50 collections. Delete one to create another.", "metric-note"));
      } else {
        for (const post of s.items) {
          const row = make("div", "", "shared-collection-post");
          const link = button(post.title || "Open post", () => {if (available(s)) {close(); hooks.openPost(post.id);}}, "shared-collection-post-link");
          link.disabled = !available(s);
          if (post.author?.display_name) link.append(make("small", post.author.display_name));
          const remove = button("Remove", () => {
            if (!removable(s)) return;
            s.confirmation = {kind:"remove", id:post.id, title:post.title || "this post"}; paint(s); el.confirm.focus();
          });
          remove.setAttribute("aria-label", `Remove ${post.title || "post"} from collection`);
          remove.disabled = !removable(s); row.append(link, remove); el.list.append(row);
        }
        if (!s.items.length) el.list.append(make("p", "No posts visible to you in this collection.", "metric-note"));
      }
    }
    async function load(s, append = false) {
      if (!current(s) || !hooks.canRead() || attemptFor(s)?.busy) return;
      const unresolved = attemptFor(s);
      if (!s.collectionId && unresolved?.unknown && unresolved.collectionId) s.collectionId = unresolved.collectionId;
      const ticket = ++s.read, live = () => current(s) && s.read === ticket;
      const cursor = append ? s.next : null, previousRevision = s.collection?.revision;
      s.loading = true; s.error = "";
      if (!append) clearContent(s);
      paint(s);
      try {
        if (!s.conversation) {
          const params = new URLSearchParams({folder:"all", limit:"50"});
          if (cursor) params.set("cursor", cursor);
          const data = await hooks.api(`/api/messages/conversations?${params}`);
          if (!live()) return;
          if (!Array.isArray(data?.items) || data.items.length > 50
              || data.next_cursor != null && (typeof data.next_cursor !== "string" || s.cursors.has(data.next_cursor))) {
            throw new Error("Conversations could not be verified. Refresh to continue.");
          }
          const known = new Set(append ? s.conversations.map(c => c.id) : []);
          s.conversations = [...(append ? s.conversations : []), ...data.items.filter(c => eligible(c) && !known.has(c.id) && known.add(c.id))];
          s.next = data.next_cursor || null;
          if (s.next) s.cursors.add(s.next);
        } else {
          if (!append) {
            const rows = collectionList(await hooks.api(base(s)), s.conversation.id);
            if (!live()) return;
            s.collections = rows;
            if (s.collectionId && !rows.some(row => row.id === s.collectionId)) {
              s.collectionId = null; s.editing = false; s.confirmation = null;
              s.notice = "That collection is no longer available. The current list is shown.";
            }
          }
          if (s.collectionId) {
            const params = new URLSearchParams({limit:"50"});
            if (cursor) {params.set("after", cursor); params.set("expected_revision", String(previousRevision));}
            const data = page(await hooks.api(`${detailPath(s, s.collectionId)}?${params}`), s);
            if (!live()) return;
            if (append && (data.collection.revision !== previousRevision
                || data.items.some(post => s.items.some(old => old.id === post.id)))) throw new Error("Collection changed. Refresh to continue.");
            s.collection = data.collection; s.items = [...(append ? s.items : []), ...data.items]; s.next = data.next_after || null;
            if (s.confirmation?.kind === "remove" && !s.items.some(post => post.id === s.confirmation.id)) s.confirmation = null;
          }
          const op = attemptFor(s);
          if (op?.unknown) {
            op.readback = true;
            if (op.kind !== "create") {
              attempts.delete(s.conversation.id);
              s.notice = "Current state loaded. Check the collection before making another change.";
            }
          }
        }
        s.ready = true;
      } catch (error) {
        if (!live()) return;
        clearContent(s); s.conversations = [];
        if ([401,403,404].includes(error.status)) deny(s);
        s.error = [401,403,404].includes(error.status) ? "These collections are no longer available to this profile."
          : error.status === 409 ? "This collection changed. Refresh to load its current posts. Your entered name is kept."
          : error.message || "Collections could not load. Refresh to try again.";
      } finally {
        if (live()) {
          s.loading = false; paint(s);
          const focused = doc.activeElement;
          if (!focused || focused === doc.body || focused.isConnected === false) el.title.focus({preventScroll:true});
        }
      }
    }
    function resetEditor(s) {s.editing = false; s.draft = ""; s.consent = false; s.confirmation = null;}
    function chooseConversation(s, conversation) {
      if (!available(s)) return;
      s.conversation = conversation; s.collectionId = null; s.notice = ""; resetEditor(s); void load(s);
    }
    function openCollection(s, id) {
      if (!available(s)) return;
      s.collectionId = id; s.notice = ""; resetEditor(s); void load(s);
    }
    function show(conversation = null, drop = null, anchor = null) {
      syncIdentity(); if (!owner() || !hooks.canRead()) {hooks.signIn(); return;}
      session = {owner:owner(), scope:scope(), epoch, conversation, drop:drop ? {id:drop.id,title:drop.title} : null,
        anchor, collectionId:null, collection:null, collections:[], conversations:[], items:[], cursors:new Set(),
        read:0, ready:false, loading:false, next:null, draft:"", consent:false, editing:false};
      hooks.openDialog(el.dialog); paint(session); void load(session);
    }
    function close() {if (el.dialog.open) el.dialog.close(); finishClose();}
    function finishClose() {
      if (el.dialog.open) return;
      const old = session; session = null; el.list.replaceChildren(); el.name.value = ""; el.consent.checked = false;
      el.title.textContent = "Shared collections";
      for (const key of ["subject", "status", "error", "confirm-text"]) el[key].textContent = "";
      if (old && accountCurrent(old)) {
        if (old.anchor?.isConnected && (!old.anchor.getClientRects || old.anchor.getClientRects().length)) old.anchor.focus();
        else hooks.restoreFocus?.(old.drop?.id);
      }
    }
    async function send(s, op) {
      if (!current(s) || op.busy || !hooks.canRead()) return;
      op.busy = true; op.readback = false; s.error = ""; s.notice = "";
      attempts.set(s.conversation.id, op); ++s.read; s.loading = false; paint(s);
      try {
        const result = await hooks.api(op.path, {...op.options, csrf:true});
        if (!accountCurrent(s)) return;
        if (op.kind === "delete") {
          if (result?.deleted !== true || result.id !== op.collectionId
              || !Number.isSafeInteger(result.revision) || result.revision <= op.revision) throw new Error("Deletion response could not be verified.");
        } else {
          const returned = collection(result?.collection, s.conversation.id);
          if (op.kind !== "create" && (returned.id !== op.collectionId || returned.revision < op.revision)) throw new Error("Change response could not be verified.");
          if (["add","remove"].includes(op.kind) && (result.drop_id !== op.dropId || result.present !== (op.kind === "add"))) throw new Error("Post change could not be verified.");
        }
        attempts.delete(s.conversation.id);
        const active = session;
        if (current(active) && active.conversation?.id === s.conversation.id) {
          resetEditor(active);
          if (op.kind === "create") active.collectionId = result.collection.id;
          if (op.kind === "delete") active.collectionId = null;
          active.notice = op.notice; await load(active);
        }
      } catch (error) {
        if (!accountCurrent(s)) return;
        op.unknown = !error.status || error.status >= 500;
        if (!op.unknown) attempts.delete(s.conversation.id);
        const active = session;
        if (current(active) && active.conversation?.id === s.conversation.id) {
          op.busy = false;
          if (op.unknown || error.status === 409) {
            active.notice = error.status === 409 ? "Someone changed this collection. Your entered name is kept; review the current state and choose again." : "";
            await load(active);
          } else {
            if ([401,403,404].includes(error.status)) deny(active);
            active.error = error.message || "The change was not saved.";
          }
        }
      } finally {
        op.busy = false;
        const active = session;
        if (current(active) && active.conversation?.id === s.conversation.id) paint(active);
      }
    }
    function mutate(s, kind, path, options, notice) {
      if (!(kind === "delete" || kind === "remove" ? removable(s) : writable(s))) return;
      return send(s, {kind, path, options, notice, name:s.draft, collectionId:s.collectionId,
        revision:s.collection?.revision, dropId:kind === "add" ? s.drop?.id : s.confirmation?.id, busy:false, unknown:false});
    }
    function events(items) {
      syncIdentity(); const s = session;
      if (!current(s) || !items.some(item => item.type === "dm.conversation.changed"
          && (!s.conversation || item.payload?.conversation_id === s.conversation.id))) return;
      // Remove old private content immediately; every read is fenced by epoch.
      clearContent(s); s.conversations = []; s.cursors.clear();
      if (s.confirmation?.kind === "remove") s.confirmation = null;
      s.notice = "Conversation updated. Review the current collection before changing it.";
      paint(s); if (!attemptFor(s)?.busy) void load(s);
    }
    el.name.addEventListener("input", () => {if (current(session)) session.draft = el.name.value;});
    el.consent.addEventListener("change", () => {if (current(session)) {session.consent = el.consent.checked; paint(session);}});
    el.form.addEventListener("submit", event => {
      event.preventDefault(); const s = session; if (!writable(s)) return;
      const name = s.draft.trim().replace(/\s+/g, " ");
      if (!name || name.length > 60 || !s.editing && !s.consent) {s.error = "Enter a name and confirm who can access the collection."; paint(s); return;}
      if (s.editing) void mutate(s, "rename", detailPath(s, s.collectionId),
        {method:"PATCH", json:{name, expected_revision:s.collection.revision}}, "Collection renamed.");
      else void mutate(s, "create", base(s), {method:"POST", json:{name, create_token:hooks.uuid(), sharing_scope:"current_and_future_members"}}, "Collection created.");
    });
    el.refresh.addEventListener("click", () => {if (current(session) && !session.loading) {session.cursors.clear(); void load(session);}});
    el.more.addEventListener("click", () => {if (available(session) && session.next) void load(session, true);});
    el.back.addEventListener("click", () => {
      const s = session; if (!current(s) || s.loading || attemptFor(s)?.busy) return;
      if (s.collectionId) s.collectionId = null; else s.conversation = null;
      resetEditor(s); s.cursors.clear(); s.notice = ""; void load(s);
    });
    el.rename.addEventListener("click", () => {
      const s = session; if (!writable(s) || !s.collection) return;
      s.editing = true; s.draft = s.collection.name; s.confirmation = null; paint(s); el.name.focus(); el.name.select();
    });
    el["cancel-edit"].addEventListener("click", () => {if (available(session)) {resetEditor(session); paint(session); el.rename.focus();}});
    el.delete.addEventListener("click", () => {const s=session;if (removable(s)) {s.confirmation={kind:"delete"};paint(s);el.confirm.focus();}});
    el.keep.addEventListener("click", () => {const s=session;if (current(s) && !attemptFor(s)?.busy) {s.confirmation=null;paint(s);el.refresh.focus();}});
    el.confirm.addEventListener("click", () => {
      const s=session; if (!removable(s) || !s.confirmation || !s.collection) return;
      const kind=s.confirmation.kind, suffix=kind === "remove" ? `/entries/${encodeURIComponent(s.confirmation.id)}` : "";
      void mutate(s, kind, `${detailPath(s,s.collectionId)}${suffix}?expected_revision=${s.collection.revision}`,
        {method:"DELETE"}, kind === "remove" ? "Post removed from collection." : "Shared collection deleted.");
    });
    el.add.addEventListener("click", () => {
      const s=session; if (!writable(s) || !s.drop || !s.collection) return;
      void mutate(s,"add",`${detailPath(s,s.collectionId)}/entries/${encodeURIComponent(s.drop.id)}`,
        {method:"PUT",json:{expected_revision:s.collection.revision}},"Post added to collection.");
    });
    el.retry.addEventListener("click", () => {
      const s=session, op=attemptFor(s);
      if (current(s) && !s.loading && hooks.canWrite() && op?.kind === "create" && op.unknown && op.readback && !op.busy) void send(s,op);
    });
    el.dialog.addEventListener("close", finishClose);
    return {show,close,syncIdentity,events};
  }
  return {create};
});
